/**
 * Font registry -------------------------------------------------------
 *
 * Central catalog of every font the Master Admin can pick from the
 * Settings → Appearance section. Each entry defines:
 *
 *   - label:   human-readable name shown in the dropdown
 *   - stack:   CSS `font-family` value applied to --app-font-sans
 *   - google:  Google Fonts `family=...` param (null = system font, no fetch)
 *
 * Adding a new font is a one-line change here — the admin UI and the
 * runtime loader both iterate the same map.
 */
export interface FontOption {
  key: string;
  label: string;
  stack: string;
  google: string | null;
  note?: string;
}

export const FONT_OPTIONS: Record<string, FontOption> = {
  instrument_sans: {
    key: "instrument_sans",
    label: "Instrument Sans",
    stack: '"Instrument Sans", ui-sans-serif, system-ui, sans-serif',
    google: "Instrument+Sans:wght@400;500;600;700",
    note: "Default — crisp UI sans",
  },
  inter: {
    key: "inter",
    label: "Inter",
    stack: '"Inter", ui-sans-serif, system-ui, sans-serif',
    google: "Inter:wght@400;500;600;700;800",
    note: "Modern neutral sans",
  },
  atkinson_hyperlegible: {
    key: "atkinson_hyperlegible",
    label: "Atkinson Hyperlegible",
    stack: '"Atkinson Hyperlegible", ui-sans-serif, system-ui, sans-serif',
    google: "Atkinson+Hyperlegible:wght@400;700",
    note: "Designed for low-vision readers (WCAG)",
  },
  open_sans: {
    key: "open_sans",
    label: "Open Sans",
    stack: '"Open Sans", ui-sans-serif, system-ui, sans-serif',
    google: "Open+Sans:wght@400;500;600;700;800",
    note: "Friendly, highly legible",
  },
  nunito: {
    key: "nunito",
    label: "Nunito",
    stack: '"Nunito", ui-sans-serif, system-ui, sans-serif',
    google: "Nunito:wght@400;600;700;800;900",
    note: "Rounded, approachable",
  },
  lora: {
    key: "lora",
    label: "Lora",
    stack: '"Lora", Georgia, "Times New Roman", serif',
    google: "Lora:wght@400;500;600;700",
    note: "Editorial serif for readability",
  },
  merriweather: {
    key: "merriweather",
    label: "Merriweather",
    stack: '"Merriweather", Georgia, "Times New Roman", serif',
    google: "Merriweather:wght@400;700;900",
    note: "Classic, high-contrast serif",
  },
  system: {
    key: "system",
    label: "System default",
    stack:
      'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    google: null,
    note: "Uses the reader's own OS font",
  },
};

export const DEFAULT_FONT_KEY = "instrument_sans";

export function resolveFont(key: string | null | undefined): FontOption {
  if (!key) return FONT_OPTIONS[DEFAULT_FONT_KEY];
  return FONT_OPTIONS[key] ?? FONT_OPTIONS[DEFAULT_FONT_KEY];
}

/**
 * Injects (or updates) a single `<link rel="stylesheet">` that loads the
 * active Google Font, then sets the CSS variable `--app-font-sans` so
 * every Tailwind `font-sans` utility picks up the new stack instantly.
 *
 * Idempotent — safe to call on every settings refresh.
 */
export function applyFont(key: string): void {
  const font = resolveFont(key);

  // 1. Load the webfont (if any) by injecting a dedicated <link>.
  const LINK_ID = "app-font-link";
  let link = document.getElementById(LINK_ID) as HTMLLinkElement | null;
  if (font.google) {
    const href = `https://fonts.googleapis.com/css2?family=${font.google}&display=swap`;
    if (!link) {
      link = document.createElement("link");
      link.id = LINK_ID;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
  } else if (link) {
    // System font chosen — tear the stylesheet down so we don't keep
    // fetching an unused font.
    link.remove();
  }

  // 2. Update the CSS variable read by Tailwind's `sans` family.
  document.documentElement.style.setProperty("--app-font-sans", font.stack);
  // Mark the key so testing / debugging can see the active choice.
  document.documentElement.dataset.fontKey = font.key;
}
